import { showSuccess } from '@calendly/alert';
import { Form } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { uniq } from 'lodash-es';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import changeSet from 'core/change_set';
import { AccordionProvider, BookingPreviewSync, DirtyTracking, editETFormValuesMapper, eventTypeEditorCrudPanelsFormConfigs, eventTypeEditorPanelsFormConfigs, formatCrudNotificationContent, mergeArraysOfObjectsByKeys, SelectedSectionsProvider, useFormInitialValidation, useGetDefaultAvailabilityRules, useResetUpdateMutationsCache, useUpdateEventTypeMutation, ValidationErrorAlert } from 'features/event_type_editor';
import { useEventTypeContext } from 'features/event_type_provider';
import { getMergedInitialValues } from './merge_initial_values';
import { formatNotificationSettingsValues } from './notification_settings';
import { useActiveTabValidation } from './use_active_tab_validation';
import { useTabValidityTracking } from './use_tab_validity_tracking';
import { getFullValidationSchema } from './validate_all';
import { AVAILABILITY_TAB_KEY, getAvailabilityActiveTabConfig } from '../tabs/availability/tab_config';
import { DEFAULT_TAB_KEY, eventTypeEditorCrudTabs } from '../tabs/registry';
import { SCHEDULING_LIMITS_TAB_KEY, getSchedulingLimitsActiveTabConfig } from '../tabs/scheduling_limits/tab_config';
const membershipsAttributeKey = 'event_type_memberships_attributes';
const valuesMapper = values => eventTypeEditorPanelsFormConfigs.moreOptions.valuesMapper(editETFormValuesMapper(values));
const withoutMemberships = ({
  [membershipsAttributeKey]: _memberships,
  ...attributes
}) => attributes;
const mergeFormattedAttributes = (...attributesByForm) => {
  const membershipsToMerge = attributesByForm.map(attributes => attributes[membershipsAttributeKey]).filter(Boolean);
  return {
    ...attributesByForm.reduce((attributes, formAttributes) => ({
      ...attributes,
      ...withoutMemberships(formAttributes)
    }), {}),
    ...(membershipsToMerge.length ? {
      [membershipsAttributeKey]: mergeArraysOfObjectsByKeys(membershipsToMerge, 'id', 'user_id', 'organization_invitation_id')
    } : {})
  };
};
const getApplicablePanelKeys = (eventType, tab) => {
  const formConfig = eventTypeEditorPanelsFormConfigs[tab.formKey];
  if (!formConfig) return [];
  const applicablePanelKeys = formConfig.getApplicablePanelKeys(eventType);
  return tab.panelKeys.filter(panelKey => applicablePanelKeys.includes(panelKey));
};
const getPanelKeysByFormKey = eventType => eventTypeEditorCrudTabs.reduce((panelKeysByFormKey, tab) => ({
  ...panelKeysByFormKey,
  [tab.formKey]: uniq([...(panelKeysByFormKey[tab.formKey] || []), ...getApplicablePanelKeys(eventType, tab)])
}), {});
const getPanelFieldNames = (panelFieldsMapping, panelKeys) => {
  const panelFieldsByKey = Object.fromEntries(panelFieldsMapping);
  return uniq(panelKeys.flatMap(panelKey => panelFieldsByKey[panelKey] || []));
};
export const getTabFieldNames = (eventType, tab) => {
  if (tab.key === SCHEDULING_LIMITS_TAB_KEY) {
    return getSchedulingLimitsActiveTabConfig(eventType).fieldNames;
  }
  const formConfig = eventTypeEditorCrudPanelsFormConfigs[tab.formKey];
  if (!formConfig) return [];
  const panelKeys = getApplicablePanelKeys(eventType, tab);
  const panelFieldsMapping = formConfig.getPanelFieldsMapping(eventType).filter(([key]) => panelKeys.includes(key));
  const fieldNames = getPanelFieldNames(panelFieldsMapping, panelKeys);

  // MeetingNameField (name + slug) renders in this tab but outside the panel/accordion
  // system, so it never shows up in the panel-derived field names above.
  if (tab.key === DEFAULT_TAB_KEY) {
    return uniq([...fieldNames, 'name', 'slug']);
  }
  return fieldNames;
};
const ActiveTabValidation = ({
  activeTabKey,
  fieldNames,
  validationSchema
}) => {
  useActiveTabValidation({
    activeTabKey,
    fieldNames,
    validationSchema
  });
  return null;
};
const FormInitialValidation = ({
  isCreation,
  panelFieldsMapping,
  formInnerRef
}) => {
  useFormInitialValidation({
    isCreation,
    panelFieldsMapping,
    formInnerRef
  });
  return null;
};
const TabValidityTracking = ({
  activeTabKey,
  tabFieldNamesByKey,
  validationSchema,
  onInvalidTabKeysChange
}) => {
  const invalidTabKeys = useTabValidityTracking({
    activeTabKey,
    tabFieldNamesByKey,
    validationSchema
  });
  useEffect(() => {
    onInvalidTabKeysChange(invalidTabKeys);
  }, [invalidTabKeys, onInvalidTabKeysChange]);
  return null;
};
const formFillCss = "fh9lkxw";
export const RootForm = ({
  activeTab,
  children,
  className,
  shellRef,
  ...rest
}) => {
  const dispatch = useDispatch();
  const {
    eventType,
    isCreation
  } = useEventTypeContext();
  const defaultAvailabilityRules = useGetDefaultAvailabilityRules();
  const [updateEventType, {
    isLoading,
    error
  }] = useUpdateEventTypeMutation({
    id: eventType.id
  });
  const formId = useMemo(() => nanoid(), []);
  const activeFormConfig = eventTypeEditorCrudPanelsFormConfigs[activeTab.formKey];
  const {
    'data-testid': dataTestId,
    ...formProps
  } = rest;
  useResetUpdateMutationsCache({
    id: eventType.id
  });
  const formInnerRef = useRef();
  const initialValues = useMemo(() => getMergedInitialValues({
    eventType,
    defaultAvailabilityRules
  }), [defaultAvailabilityRules, eventType]);
  const validationSchema = useMemo(() => getFullValidationSchema({
    eventType
  }), [eventType]);
  const activePanelKeys = useMemo(() => getApplicablePanelKeys(eventType, activeTab), [activeTab, eventType]);
  const availabilityActiveTabConfig = useMemo(() => activeTab.key === AVAILABILITY_TAB_KEY ? getAvailabilityActiveTabConfig(eventType) : null, [activeTab.key, eventType]);
  const schedulingLimitsActiveTabConfig = useMemo(() => activeTab.key === SCHEDULING_LIMITS_TAB_KEY ? getSchedulingLimitsActiveTabConfig(eventType) : null, [activeTab.key, eventType]);
  const activePanelFieldsMapping = useMemo(() => activeFormConfig.getPanelFieldsMapping(eventType).filter(([key]) => activePanelKeys.includes(key)), [activeFormConfig, activePanelKeys, eventType]);
  const activePanelFields = useMemo(() => availabilityActiveTabConfig?.fieldNames || schedulingLimitsActiveTabConfig?.fieldNames || getPanelFieldNames(activePanelFieldsMapping, activePanelKeys), [activePanelFieldsMapping, activePanelKeys, availabilityActiveTabConfig, schedulingLimitsActiveTabConfig]);
  const [invalidTabKeys, setInvalidTabKeys] = useState(() => new Set());
  const tabFieldNamesByKey = useMemo(() => Object.fromEntries(eventTypeEditorCrudTabs.map(tab => [tab.key, getTabFieldNames(eventType, tab)])), [eventType]);
  const activeValidationSchema = useMemo(() => availabilityActiveTabConfig?.validationSchema || schedulingLimitsActiveTabConfig?.validationSchema || activeFormConfig.getValidationSchemaForPanelKeys(eventType, activePanelKeys), [activeFormConfig, activePanelKeys, eventType, availabilityActiveTabConfig, schedulingLimitsActiveTabConfig]);
  const etLocks = useMemo(() => activeFormConfig.getEventTypeLocks(eventType), [activeFormConfig, eventType]);
  const panelKeysByFormKey = useMemo(() => getPanelKeysByFormKey(eventType), [eventType]);
  const lockingDisables = Object.values(etLocks).every(Boolean);
  const defaultOpenPanelKeys = useMemo(() => activePanelKeys.slice(0, 1), [activePanelKeys]);
  const formatValuesToServerData = useCallback(values => ({
    ...mergeFormattedAttributes(...Object.entries(panelKeysByFormKey).map(([formKey, panelKeys]) => eventTypeEditorCrudPanelsFormConfigs[formKey].getFormatValuesToServerData(eventType)(values, panelKeys, initialValues)), formatNotificationSettingsValues(values, eventType), formatCrudNotificationContent({
      eventType,
      values,
      initialValues
    })),
    public: values.isPublic,
    locale: values.locale
  }), [eventType, initialValues, panelKeysByFormKey]);
  const handleSubmit = useCallback((values, form) => {
    if (isLoading) return Promise.resolve();
    const isFormDirty = changeSet.isDirty(formId);
    const hasUserToggledNotetaker = Boolean(values?.hasUserToggledNotetaker);
    const shouldSubmitForNotetakerOnlyChange = !isCreation && !isFormDirty && typeof values?.notetakerEnabledForEt === 'boolean' && hasUserToggledNotetaker;
    if (!isFormDirty && !isCreation && !shouldSubmitForNotetakerOnlyChange) {
      const message = eventType.active ? I18n.t('publisher.event_type_editor.saved') : I18n.t('publisher.event_type_editor.published');
      dispatch(showSuccess(message));
      return Promise.resolve();
    }
    return Promise.resolve(updateEventType({
      eventType,
      ...(!eventType.active ? {
        successMessage: I18n.t('publisher.event_type_editor.published')
      } : {}),
      attributes: formatValuesToServerData(values)
    })).then(result => {
      form?.setFieldTouched?.('notetakerEnabledForEt', false, false);
      form?.setFieldValue?.('hasUserToggledNotetaker', false, false);
      return result;
    });
  }, [dispatch, eventType, formId, formatValuesToServerData, isCreation, isLoading, updateEventType]);
  const form = <SelectedSectionsProvider resetOnChangeOf={eventType.id}>
      <AccordionProvider allowMultiple={false} defaultOpenKeys={defaultOpenPanelKeys}>
        <Form {...formProps} className={dataTestId ? formFillCss : className} enableReinitialize externalError={error} initialValues={initialValues} innerRef={formInnerRef} validateOnChange={false} validationSchema={validationSchema} onSubmit={handleSubmit}>
          <FormInitialValidation isCreation={isCreation} panelFieldsMapping={activePanelFieldsMapping} formInnerRef={formInnerRef} />
          <ActiveTabValidation activeTabKey={activeTab.key} fieldNames={activePanelFields} validationSchema={activeValidationSchema} />
          <TabValidityTracking activeTabKey={activeTab.key} tabFieldNamesByKey={tabFieldNamesByKey} validationSchema={validationSchema} onInvalidTabKeysChange={setInvalidTabKeys} />
          <BookingPreviewSync />
          <DirtyTracking formId={formId} valuesMapper={valuesMapper} />
          <ValidationErrorAlert />
          {children({
          etLocks,
          invalidTabKeys: Array.from(invalidTabKeys),
          lockingDisables,
          panelKeys: activePanelKeys,
          panelPropsByKey: activeFormConfig.panelPropsByKey
        })}
        </Form>
      </AccordionProvider>
    </SelectedSectionsProvider>;
  if (dataTestId || shellRef) {
    return <div ref={shellRef} className={className} data-testid={dataTestId}>
        {form}
      </div>;
  }
  return form;
};

require("./root_form.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./root_form.js");